import find from 'lodash.find';
import groupBy from 'lodash.groupby';
import React from 'react';

import { Dictionary } from '@/services/types';
import { ChosenSpec, ISpecGroup } from '@/types/product';

import { filterValidFullProducts } from '../../../../../services/products';
import { IProduct } from '../../../../../store/ducks/products/types';
import { IShopConfig } from '../../../../../store/ducks/shopConfig/types';

export const getGroupByFp = (fp: IProduct): ISpecGroup => {
  const { id, product_id, data } = fp;
  const { color, size } = data?.spec ?? {};

  return {
    images: fp.images || [],
    fullProductId: id,
    productId: product_id,
    color: color || '',
    size: size || undefined,
  };
};

export const fillWithMissingColors = (
  colors: string[],
  group: IProduct[],
  fullProducts: IProduct[],
) => {
  colors.forEach((color) => {
    const hasColorInGroup = group.find((fp) => fp.data?.spec.color === color);
    const fpWithTheColor = fullProducts.find((fp) => fp.data?.spec.color === color);

    if (!hasColorInGroup && fpWithTheColor) group.push(fpWithTheColor);
  });
};

export const handleGroupedWithoutSize = (
  fullProducts: IProduct[],
  setSpecs: React.Dispatch<
    React.SetStateAction<{ size: string | undefined; groups: ISpecGroup[] }[]>
  >,
) => {
  const groupedColor = groupBy(fullProducts, 'data.spec.color');

  (Object.keys(groupedColor) as (keyof Dictionary<IProduct[]>)[]).forEach((color) => {
    const group: any = groupedColor[color];

    if (group?.length) {
      setSpecs((old) => [
        ...old,
        { size: undefined, groups: group.map((fp: IProduct) => getGroupByFp(fp)) },
      ]);
    }
  });
};

export const handleGroupedSizeAndColor = (
  fullProducts: IProduct[],
  setSpecs: React.Dispatch<
    React.SetStateAction<{ size: string | undefined; groups: ISpecGroup[] }[]>
  >,
) => {
  const groupedSize = groupBy(fullProducts, 'data.spec.size');
  const colors = Object.keys(groupBy(fullProducts, 'data.spec.color'));

  (Object.keys(groupedSize) as (keyof Dictionary<IProduct[]>)[]).forEach((size) => {
    const group: any = groupedSize[size];

    if (group) {
      fillWithMissingColors(colors, group, fullProducts);
      const newOne = {
        size: size.toString(),
        groups: group.map((fp: IProduct) => getGroupByFp(fp)),
      };

      setSpecs((old) => [...old, newOne]);
    }
  });
};

export const fillSpecs = (
  fullProducts: IProduct[],
  setSpecs: React.Dispatch<
    React.SetStateAction<{ size: string | undefined; groups: ISpecGroup[] }[]>
  >,
) => {
  setSpecs([]);
  const doesNotHaveSize = Object.keys(groupBy(fullProducts, 'data.spec.size')).every(
    (key) => key === 'undefined',
  );

  if (doesNotHaveSize) handleGroupedWithoutSize(fullProducts, setSpecs);
  else handleGroupedSizeAndColor(fullProducts, setSpecs);
};

export const fillChosenSpec = (
  fullProducts: IProduct[],
  shop: IShopConfig,
  cartItem: any,
  setChosenSpec: React.Dispatch<React.SetStateAction<ChosenSpec>>,
) => {
  const validFullProducts = fullProducts ? filterValidFullProducts(fullProducts, shop) : [];

  const fp = cartItem ? fullProducts.find((fp) => fp.ean === cartItem.ean) : validFullProducts[0];

  if (fp) {
    setChosenSpec({
      size: fp.data!.spec.size,
      group: {
        fullProductId: fp.id,
        productId: fp.product_id,
        color: fp.data!.spec.color,
        size: fp.data!.spec.size,
        images: fp.images,
      },
    });
  }
};

export const handleChangeSpec = (
  { size, group = null }: { size: string | undefined; group: ISpecGroup | null },
  specs: { size: string | undefined; groups: ISpecGroup[] }[],
  setChosenSpec: React.Dispatch<React.SetStateAction<ChosenSpec>>,
) => {
  if (!group) {
    const getGroup = () => {
      const currentSpec = specs.find((spec) => spec.size === size) ?? null;

      if (currentSpec && currentSpec.groups.length > 0) {
        return (
          currentSpec.groups
            .sort((a, b) => (a.color > b.color ? 1 : -1))
            .find((group) => group.size === size) ?? currentSpec.groups[0]
        );
      }

      return null;
    };

    const group = getGroup();

    if (group) setChosenSpec({ size, group });
  } else if (!size) {
    const getGroupAndSize = () => {
      const currentSpec = find(specs, { groups: [{ color: group.color }] });

      if (currentSpec && currentSpec.groups.length > 0) {
        return { size: currentSpec.size, newGroup: currentSpec.groups[0] };
      }

      return { size: null, newGroup: null };
    };

    const { size, newGroup } = getGroupAndSize();

    if (newGroup) setChosenSpec({ size, group: newGroup });
  } else {
    setChosenSpec({ size: group.size, group });
  }
};
